import { notification } from "antd";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { getVendorFirebaseAppForStore } from "auth/storeFirebaseBootstrap";
import VendorPushService from "./VendorPushService";

let foregroundUnsubscribe = null;
let activeFcmToken = null;

const TOKEN_STORAGE_KEY = "vendor_fcm_token";

function getStoredToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY) || "";
}

function setStoredToken(token) {
  if (!token) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    return;
  }
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

function showOrderNotification(payload) {
  const title =
    payload?.notification?.title || "New order received";
  const body =
    payload?.notification?.body || "A new order has been received.";
  const orderId = payload?.data?.order_id;
  notification.info({
    message: title,
    description: `${body}${orderId ? ` (Order #${orderId})` : ""}`,
    placement: "topRight",
  });
}

async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    throw new Error("Service worker is not supported in this browser.");
  }
  return navigator.serviceWorker.register("/firebase-messaging-sw.js");
}

async function postFirebaseConfigToServiceWorker(config) {
  if (!navigator.serviceWorker) return;
  const registration = await navigator.serviceWorker.ready;
  if (registration?.active) {
    registration.active.postMessage({
      type: "INIT_FIREBASE_MESSAGING",
      config,
    });
  }
}

export async function startVendorPushNotifications() {
  const storeId = localStorage.getItem("store_id");
  if (!storeId) return;

  const supported = await isSupported();
  if (!supported) return;
  if (Notification.permission === "denied") return;

  const { app, payload } = await getVendorFirebaseAppForStore(storeId);
  if (!payload?.push_enabled) return;
  const vapidKey = payload?.vapid_key || "";
  if (!vapidKey) return;

  const swRegistration = await ensureServiceWorker();
  if (Notification.permission !== "granted") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;
  }

  await postFirebaseConfigToServiceWorker(payload?.config || {});

  const messaging = getMessaging(app);
  const token = await getToken(messaging, {
    vapidKey,
    serviceWorkerRegistration: swRegistration,
  });
  if (!token) return;

  activeFcmToken = token;
  const storedToken = getStoredToken();
  if (storedToken !== token) {
    await VendorPushService.registerToken({ token, platform: "web" });
    setStoredToken(token);
  }

  if (!foregroundUnsubscribe) {
    foregroundUnsubscribe = onMessage(messaging, (payloadMsg) => {
      showOrderNotification(payloadMsg);
    });
  }
}

export async function stopVendorPushNotifications() {
  const token = activeFcmToken || getStoredToken();
  if (token) {
    try {
      await VendorPushService.deactivateToken({ token });
    } catch (_e) {
      // noop
    }
  }
  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
    foregroundUnsubscribe = null;
  }
  activeFcmToken = null;
  setStoredToken("");
}
